import { Injectable, signal } from '@angular/core';
import { filterNil } from '@ngneat/elf';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  delay,
  filter,
  map,
  Observable,
  observeOn,
  of,
  queueScheduler,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { ReadonlyState } from '../models/form.model';
import { RendererFormsRepository } from '../repositories/renderer-forms.repository';
import { QuestionValueChangeService } from './question-value-change.service';

const WAIT_FOR_RELATED_QUESTION_UPDATE_MS = 1500;

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class QuestionValueSyncService {
  private updateQueueSubject = new BehaviorSubject<
    Map<string, Promise<string>>
  >(new Map());
  private updateQueue$ = this.updateQueueSubject.asObservable();
  private patchQuestionSubject = new BehaviorSubject<string>(undefined);
  private patchQuestion$ = this.patchQuestionSubject.asObservable();
  private patchedQuestionsSubject = new BehaviorSubject<Set<string>>(new Set());
  private patchedQuestions$ = this.patchedQuestionsSubject.asObservable();
  private isSavingSubject = new BehaviorSubject<boolean>(false);
  private isSaving$ = this.isSavingSubject.asObservable();
  isSavingUnsyncedAnswers = signal(false);

  constructor(
    private questionValueChangeService: QuestionValueChangeService,
    private rendererFormRepository: RendererFormsRepository,
  ) {
    this.enqueueUpdateQuestionOnValueChanges();
    this.dequeueUpdateQuestionOnValueSavedToCache();
    this.markingQuestionAsPatched();
  }

  syncUnsavedAnswers(): Observable<boolean> {
    if (this.isSavingUnsyncedAnswers()) return;
    this.isSavingUnsyncedAnswers.set(true);
    this.isSavingSubject.next(true);
    return this.savingDone();
  }

  markQuestionAsPatched(questionId: string): void {
    this.patchQuestionSubject.next(questionId);
  }

  private savingDone(): Observable<boolean> {
    return combineLatest([
      this.updateQueue$,
      this.patchedQuestions$,
      this.isSaving$,
    ]).pipe(
      observeOn(asyncScheduler),
      map(
        ([updateQueue, patchedQuestions, isSaving]) =>
          !isSaving &&
          updateQueue.size === 0 &&
          patchedQuestions.size === 0 &&
          !this.isSavingUnsyncedAnswers(),
      ),
      switchMap((isDone) => {
        if (isDone) return of(isDone);
        return timer(WAIT_FOR_RELATED_QUESTION_UPDATE_MS).pipe(
          tap(() => {
            this.resetUpdateQueueOnSavingDone();
          }),
          map(() => isDone),
        );
      }),
    );
  }

  private enqueue(questionId: string): void {
    if (this.rendererFormRepository.getReadonly() !== ReadonlyState.Locked) {
      queueScheduler.schedule(() => {
        const currentUpdateQueue = this.updateQueueSubject.getValue();
        if (!currentUpdateQueue.has(questionId)) {
          const savePromise = new Promise<string>((resolve) => {
            resolve(questionId);
          });
          currentUpdateQueue.set(questionId, savePromise);
          this.updateQueueSubject.next(currentUpdateQueue);
        }
      });
    }
  }

  private async dequeue(questionId: string): Promise<string> {
    const currentUpdateQueue = this.updateQueueSubject.getValue();
    if (!currentUpdateQueue.has(questionId)) {
      return;
    }
    return currentUpdateQueue.get(questionId);
  }

  private enqueueUpdateQuestionOnValueChanges(): void {
    this.questionValueChangeService.valueChanges$
      .pipe(untilDestroyed(this))
      .subscribe((answer) => {
        if (this.isSavingUnsyncedAnswers()) {
          this.enqueue(answer.questionId);
        }
      });
  }

  private dequeueUpdateQuestionOnValueSavedToCache(): void {
    this.patchedQuestions$
      .pipe(
        untilDestroyed(this),
        filter((patchedArray) => patchedArray.size > 0),
        delay(WAIT_FOR_RELATED_QUESTION_UPDATE_MS),
        tap((patchedArray) => {
          const questionId = [...patchedArray].pop();
          if (questionId) {
            this.dequeue(questionId).then((id) => {
              const currentUpdateQueue = this.updateQueueSubject.getValue();
              currentUpdateQueue.delete(id);
              this.updateQueueSubject.next(currentUpdateQueue);
              const currentPatched = this.patchedQuestionsSubject.getValue();
              currentPatched.delete(id);
              this.patchedQuestionsSubject.next(currentPatched);
            });
          }
          this.resetUpdateQueueOnSavingDone();
        }),
      )
      .subscribe();
  }

  private resetUpdateQueueOnSavingDone(): void {
    const currentUpdateQueue = this.updateQueueSubject.getValue();
    const currentPatched = this.patchedQuestionsSubject.getValue();
    if (currentUpdateQueue.size === 0 && currentPatched.size === 0) {
      this.isSavingSubject.next(false);
      this.isSavingUnsyncedAnswers.set(false);
    }
  }

  private markingQuestionAsPatched(): void {
    this.patchQuestion$
      .pipe(filterNil(), untilDestroyed(this))
      .subscribe((questionId) => {
        const currentUpdateQueue = this.updateQueueSubject.getValue();
        if (currentUpdateQueue.has(questionId)) {
          const questionPromise = currentUpdateQueue.get(questionId);
          questionPromise.then((id) => {
            const currentPatched = this.patchedQuestionsSubject.getValue();
            if (!currentPatched.has(id)) {
              currentPatched.add(id);
              this.patchedQuestionsSubject.next(currentPatched);
            }
          });
        }
      });
  }
}
